import React from "react";
import { useNavigate } from "react-router-dom";

const PRIMARY = "rgb(18, 76, 114)";

// Table-view variant of the all-hiring-request list (alternative to HiringRequestCards).
// Same data, same click-through to request details; laid out as a table.
const HiringRequestTable = ({
  filteredHiringRequests,
  demandPlans,
  hiringManagers,
  visibleCount,
  DemandPlanning,
  departments,
}) => {
  const navigate = useNavigate();

  // Preserve the card ordering: drafts first, then the rest, limited by visibleCount.
  const draftRequests = filteredHiringRequests.filter((r) => r.status === "Draft");
  const remaining = visibleCount - draftRequests.length;
  const nonDraft = filteredHiringRequests.filter((r) => r.status !== "Draft").slice(0, Math.max(0, remaining));
  const rows = [...draftRequests, ...nonDraft];

  const openRequest = (req) => {
    navigate(`/request-details?id=${req.request_id}`, {
      state: { request: req, demandPlans, DemandPlanning, departments, hiringManagers },
    });
  };

  const roleOf = (req) =>
    req.request_type === "Forecast"
      ? demandPlans.find((dp) => Number(dp.demand_id) === Number(req.demand_id))?.role
      : req.role_text;

  const managerOf = (req) => {
    const m = hiringManagers.find((x) => Number(x.user_id) === Number(req.hiring_manager));
    return m ? `${m.first_name || ""} ${m.last_name || ""}`.trim() : "";
  };

  const departmentOf = (req) =>
    (departments || []).find((d) => Number(d.department_id) === Number(req.department_id))?.department_name ||
    req.department_name || "";

  const ApprovalBadges = ({ req }) => {
    if (!((req.request_type === "Exceptional" || req.request_type === "Outsourced") &&
          req.isFinanceManagerApproveRequired === true &&
          (req.status === "Submitted" || req.status === "Approved"))) return null;
    const badge = (done, label) => (
      <span
        className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
        style={{
          backgroundColor: done ? "rgba(16,185,129,0.12)" : "rgba(245,158,11,0.12)",
          color: done ? "#059669" : "#d97706",
        }}
      >
        {done ? `✓ ${label}` : `● ${label}`}
      </span>
    );
    return (
      <span className="inline-flex gap-1 flex-wrap ml-2">
        {badge(req.isFinanceManagerApproved, "Finance")}
        {badge(req.isHRDirectorApproved, "HR Dir")}
        {badge(req.isCEOApproved, "CEO")}
      </span>
    );
  };

  return (
    <div className="overflow-x-auto px-2 sm:px-4 pb-2 sm:pb-4">
      <table className="min-w-full table-auto border-collapse">
        <thead className="bg-primary-800/10 text-primary-800 text-left">
          <tr>
            <th className="py-3 px-4 border-r border-primary-500/20">Reference #</th>
            <th className="py-3 px-4 border-r border-primary-500/20">Department</th>
            <th className="py-3 px-4 border-r border-primary-500/20">Role</th>
            <th className="py-3 px-4 border-r border-primary-500/20">Grade</th>
            <th className="py-3 px-4 border-r border-primary-500/20">Type</th>
            <th className="py-3 px-4 border-r border-primary-500/20">Hiring Manager</th>
            <th className="py-3 px-4 border-r border-primary-500/20">Planned Start</th>
            <th className="py-3 px-4">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((req, index) => (
            <tr
              key={req.request_id}
              onClick={() => openRequest(req)}
              className={`${index % 2 === 0 ? "bg-white" : "bg-primary-800/5"} hover:bg-primary-100/20 transition border-b cursor-pointer text-sm text-primary-600`}
            >
              <td className="px-4 py-3 border-r font-bold text-primary-800">{req.referenceNumber}</td>
              <td className="px-4 py-3 border-r text-[#6B6B6B]">{departmentOf(req) || "—"}</td>
              <td className="px-4 py-3 border-r text-[#232323] font-medium">{roleOf(req) || "—"}</td>
              <td className="px-4 py-3 border-r text-[#6B6B6B]">{req.position || "—"}</td>
              <td className="px-4 py-3 border-r text-[#6B6B6B]">{req.request_type}</td>
              <td className="px-4 py-3 border-r text-[#6B6B6B]">{managerOf(req) || "—"}</td>
              <td className="px-4 py-3 border-r text-[#6B6B6B]">
                {req.planned_start_date ? new Date(req.planned_start_date).toLocaleDateString() : "—"}
              </td>
              <td className="px-4 py-3">
                <span className="inline-flex items-center gap-1.5 flex-wrap">
                  <span className={`${req.status === "Draft" ? "bg-yellow-500" : "bg-green-500"} inline-block w-2.5 h-2.5 rounded-full flex-shrink-0`} />
                  <span className="whitespace-nowrap">{req.status}</span>
                  <ApprovalBadges req={req} />
                </span>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={8} className="text-center text-gray-500 py-6">No hiring requests found.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default HiringRequestTable;
